/**
 * OTT TV preferences: read-only customer profile + Account Credit tab.
 */

const ACTIVATION_CODE_STORAGE_KEY = 'ott_tv_activation_code';
const PREFERENCES_TAG = 'ott-tv-preferences';

class OttTvPreferences extends HTMLElement {
  /** @type {HTMLElement | null} */
  #tabList = null;

  connectedCallback() {
    this.#tabList = this.closest('.ott-tv-preferences')?.querySelector('[role="tablist"]') || null;
    this.#bindTabs();
  }

  disconnectedCallback() {
    this.#tabList?.removeEventListener('click', this.#handleTabClick);
    this.#tabList?.removeEventListener('keydown', this.#handleTabKeydown);
  }

  /**
   * Customer payload for device pairing / preferences APIs.
   * @returns {{
   *   customerId: string,
   *   firstName: string,
   *   lastName: string,
   *   email: string,
   *   phone: string,
   *   activationCode: string
   * }}
   */
  getCustomerPayload() {
    return {
      customerId: (this.dataset.customerId || '').trim(),
      firstName: (this.dataset.customerFirstName || '').trim(),
      lastName: (this.dataset.customerLastName || '').trim(),
      email: (this.dataset.customerEmail || '').trim(),
      phone: (this.dataset.customerPhone || '').trim(),
      activationCode: this.getActivationCode(),
    };
  }

  /**
   * @returns {string}
   */
  getActivationCode() {
    try {
      return (sessionStorage.getItem(ACTIVATION_CODE_STORAGE_KEY) || '').trim();
    } catch {
      return '';
    }
  }

  #bindTabs() {
    if (!this.#tabList) {
      return;
    }

    this.#tabList.addEventListener('click', this.#handleTabClick);
    this.#tabList.addEventListener('keydown', this.#handleTabKeydown);
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getTabs() {
    return Array.from(this.#tabList?.querySelectorAll('[role="tab"][data-ott-tab]') || []);
  }

  /**
   * @param {string} tabId
   */
  #activateTab(tabId) {
    const tabs = this.#getTabs();
    const panels = Array.from(this.querySelectorAll('[data-ott-tab-panel]'));

    for (const tab of tabs) {
      const isActive = tab.getAttribute('data-ott-tab') === tabId;
      tab.classList.toggle('ott-tv-preferences__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    }

    for (const panel of panels) {
      const isActive = panel.getAttribute('data-ott-tab-panel') === tabId;
      panel.hidden = !isActive;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleTabClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = target.closest('[role="tab"][data-ott-tab]');
    if (!(tab instanceof HTMLElement) || !this.#tabList?.contains(tab)) {
      return;
    }

    const tabId = tab.getAttribute('data-ott-tab');
    if (tabId) {
      this.#activateTab(tabId);
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleTabKeydown = (event) => {
    const tabs = this.#getTabs();
    if (!tabs.length) {
      return;
    }

    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
    if (currentIndex < 0) {
      return;
    }

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      event.preventDefault();
      nextIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    const nextTab = tabs[nextIndex];
    const tabId = nextTab?.getAttribute('data-ott-tab');
    if (nextTab && tabId) {
      this.#activateTab(tabId);
      nextTab.focus();
    }
  };
}

function init() {
  if (!customElements.get(PREFERENCES_TAG)) {
    customElements.define(PREFERENCES_TAG, OttTvPreferences);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
